import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/modern/utils";

// Shared radius/border live on each non-mosaic variant — not on the cva base —
// so mosaic can own `rounded-2xl` / borderless chrome without fighting
// `rounded-swivu-base` (custom radius is not in twMerge's default rounded group).
const cardVariants = cva("", {
  variants: {
    variant: {
      default: "rounded-swivu-base border bg-card text-card-foreground",
      primary: "rounded-swivu-base border border-primary bg-primary text-primary-foreground",
      "primary-outline": "rounded-swivu-base border border-primary bg-primary/10 text-primary",
      success: "rounded-swivu-base border border-success bg-success text-success-foreground",
      "success-outline": "rounded-swivu-base border border-success bg-success/10 text-success",
      degraded: "rounded-swivu-base border border-degraded bg-degraded text-degraded-foreground",
      "degraded-outline": "rounded-swivu-base border border-degraded bg-degraded/10 text-degraded",
      ai: "rounded-swivu-base border border-ai bg-ai text-ai-foreground",
      "ai-outline": "rounded-swivu-base border border-ai bg-ai/10 text-ai",
      outline: "rounded-swivu-base border border-border text-foreground bg-transparent",
      modern: "rounded-swivu-base border bg-card shadow-none",
      ghost: "rounded-swivu-base bg-transparent hover:bg-accent border-none shadow-none transition-colors",
      transparent: "rounded-swivu-base bg-transparent border-none shadow-none",
      // Compose fills with className (bg-ai, bg-cobalt, bg-muted, …) per docs/design/design.md.
      // Optional hero radius: className="rounded-3xl". Nested CardHeader/Content: use p-0 or put content directly in Card.
      mosaic: "rounded-2xl border-0 shadow-none p-5 bg-transparent text-foreground"
    },
    size: {
      default: "",
      sm: "p-2",
      base: "p-swivu-base"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});

export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(({ className, variant, size, ...props }, ref) => <div ref={ref} className={cn(cardVariants({ variant, size }), className)} {...props} />);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex flex-col space-y-1.5 p-swivu-base", className)} {...props} />
));
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-2xl font-normal leading-none tracking-tight", className)} {...props} />
));
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-swivu-base pt-0", className)} {...props} />
));
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center p-swivu-base pt-0", className)} {...props} />
));
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
